import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AddOutlined as AddOutlinedIcon,
  Close as CloseIcon,
  CancelRounded as CancelRoundedIcon,
  DeleteOutline as DeleteOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  DirectionsCarOutlined as DirectionsCarOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  GroupOutlined as GroupOutlinedIcon,
  HelpOutlineOutlined as HelpOutlineOutlinedIcon,
  CheckRounded as CheckRoundedIcon,
  ScheduleRounded as ScheduleRoundedIcon,
  KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon,
  Search as SearchIcon,
  CorporateFareOutlined as CorporateFareOutlinedIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

import settingsApi from "Services/Settings";

const TAB_OPTIONS = [
  {
    value: "DRIVER",
    label: "Driver Types",
    subtitle: "Manage document types for drivers",
    icon: DirectionsCarOutlinedIcon,
    helper: "type_user = DRIVER",
    accent: "#16a34a",
    accentSoft: "#edfdf3",
  },
  {
    value: "USER",
    label: "User Types",
    subtitle: "Manage document types for users",
    icon: GroupOutlinedIcon,
    helper: "type_user = USER",
    accent: "#2563eb",
    accentSoft: "#eef4ff",
  },
  {
    value: "CORPORATE",
    label: "Corporate Types",
    subtitle: "Manage document types for corporate users",
    icon: CorporateFareOutlinedIcon,
    helper: "type_user = CORPORATE",
    accent: "#9333ea",
    accentSoft: "#f5f3ff",
  },
];

const normalizeText = (value) => (value ?? "").toString().trim();

const normalizeKey = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeTypeUser = (value) => {
  const normalized = normalizeText(value).toUpperCase();
  if (normalized === "DRIVER" || normalized === "USER" || normalized === "CORPORATE") {
    return normalized;
  }

  return "";
};

const resolveDocumentTypeRows = (response) => {
  const candidates = [
    response?.data?.data,
    response?.data?.data?.data,
    response?.data?.data?.items,
    response?.data?.items,
    response?.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (Array.isArray(candidate?.data)) {
      return candidate.data;
    }
  }

  return [];
};

const isSuccessResponse = (response) =>
  response?.status === 200 ||
  response?.status === 201 ||
  response?.data?.status === true ||
  response?.data?.success === true;

const getStatusMeta = (status) => {
  const rawStatus = normalizeText(status);
  const normalized = normalizeKey(rawStatus);
  const lowerStatus = rawStatus.toLowerCase();

  if (
    !rawStatus ||
    lowerStatus.includes("no document") ||
    normalized.includes("missing") ||
    normalized.includes("not_found") ||
    normalized.includes("rejected") ||
    normalized.includes("expired") ||
    normalized.includes("banned") ||
    normalized.includes("disabled")
  ) {
    return {
      label: rawStatus || "No document found",
      tone: "error",
      icon: <CancelRoundedIcon fontSize="small" />,
    };
  }

  if (
    normalized.includes("approved") ||
    normalized.includes("valid") ||
    normalized.includes("active") ||
    normalized.includes("success")
  ) {
    return {
      label: rawStatus,
      tone: "success",
      icon: <CheckRoundedIcon fontSize="small" />,
    };
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("boarding") ||
    normalized.includes("waiting") ||
    normalized.includes("review")
  ) {
    return {
      label: rawStatus,
      tone: "warning",
      icon: <ScheduleRoundedIcon fontSize="small" />,
    };
  }

  return {
    label: rawStatus,
    tone: "info",
    icon: <HelpOutlineOutlinedIcon fontSize="small" />,
  };
};

const getTypeUserMeta = (typeUser) => {
  const normalized = normalizeTypeUser(typeUser);

  if (normalized === "DRIVER") {
    return {
      label: "DRIVER",
      tone: "success",
      icon: <DirectionsCarOutlinedIcon fontSize="small" />,
    };
  }

  if (normalized === "USER") {
    return {
      label: "USER",
      tone: "primary",
      icon: <GroupOutlinedIcon fontSize="small" />,
    };
  }

  if (normalized === "CORPORATE") {
    return {
      label: "CORPORATE",
      tone: "corporate",
      icon: <CorporateFareOutlinedIcon fontSize="small" />,
    };
  }

  return {
    label: "N/A",
    tone: "default",
    icon: <HelpOutlineOutlinedIcon fontSize="small" />,
  };
};

const getRowId = (row) => row?.id || row?.documentTypeId || row?._id;

const getTypeUserChipStyles = (tone) => {
  if (tone === "success") {
    return {
      backgroundColor: "#edfdf3",
      color: "#15803d",
      borderColor: "#ccefd9",
    };
  }

  if (tone === "primary") {
    return {
      backgroundColor: "#eef4ff",
      color: "#2563eb",
      borderColor: "#dbe7ff",
    };
  }

  if (tone === "corporate") {
    return {
      backgroundColor: "#f5f3ff",
      color: "#9333ea",
      borderColor: "#e9d5ff",
    };
  }

  return {
    backgroundColor: "#f4f6f8",
    color: "#64748b",
    borderColor: "#e2e8f0",
  };
};

const getStatusChipStyles = (tone) => {
  if (tone === "success") {
    return {
      backgroundColor: "#edfdf3",
      color: "#15803d",
      borderColor: "#ccefd9",
    };
  }

  if (tone === "warning") {
    return {
      backgroundColor: "#fff7e6",
      color: "#b45309",
      borderColor: "#fde3b8",
    };
  }

  if (tone === "error") {
    return {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      borderColor: "#fecaca",
    };
  }

  return {
    backgroundColor: "#eff6ff",
    color: "#2563eb",
    borderColor: "#dbe7ff",
  };
};

const DocumentTypesSettings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [activeTypeUser, setActiveTypeUser] = useState("DRIVER");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const activeTabConfig = useMemo(
    () => TAB_OPTIONS.find((option) => option.value === activeTypeUser) || TAB_OPTIONS[0],
    [activeTypeUser]
  );

  const refreshDocumentTypes = useCallback(
    async (typeUser) => {
      setLoading(true);
      dispatch(showLoader("Loading please wait..."));

      try {
        const response = await settingsApi.getDocumentTypes(typeUser);
        const rows = resolveDocumentTypeRows(response);

        setDocumentTypes(Array.isArray(rows) ? rows : []);
      } catch (error) {
        dispatch(
          showSnackbar({
            message: error?.response?.data?.message || "Failed to fetch document types",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        setDocumentTypes([]);
      } finally {
        setLoading(false);
        dispatch(hideLoader());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    refreshDocumentTypes(activeTypeUser);
  }, [activeTypeUser, refreshDocumentTypes]);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    setMenuRow(null);
  }, []);

  const handleTabChange = useCallback(
    (typeUser) => {
      if (typeUser === activeTypeUser) {
        return;
      }

      closeMenu();
      setSearchTerm("");
      setPage(0);
      setSelectedRow(null);
      setDialogOpen(false);
      setDeleteDialogOpen(false);
      setDialogMode("create");
      setActiveTypeUser(typeUser);
    },
    [activeTypeUser, closeMenu]
  );

  const openCreateDialog = useCallback(() => {
    setDialogMode("create");
    setSelectedRow(null);
    setDialogOpen(true);
    closeMenu();
  }, [closeMenu]);

  const openEditDialog = useCallback(
    (row) => {
      if (!row) {
        return;
      }

      setDialogMode("edit");
      setSelectedRow(row);
      setDialogOpen(true);
      closeMenu();
    },
    [closeMenu]
  );

  const openDeleteDialog = useCallback(
    (row) => {
      if (!row) {
        return;
      }

      setSelectedRow(row);
      setDeleteDialogOpen(true);
      closeMenu();
    },
    [closeMenu]
  );

  const closeFormDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogMode("create");
    setSelectedRow(null);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  }, []);

  const formInitialValues = useMemo(
    () => ({
      documentType:
        dialogMode === "edit" ? normalizeText(selectedRow?.documentType) : "",
      documentTypeAlias:
        dialogMode === "edit" ? normalizeText(selectedRow?.documentTypeAlias) : "",
    }),
    [dialogMode, selectedRow]
  );

  const handleSaveDocumentType = useCallback(
    async (values) => {
      const payload = {
        documentType: normalizeText(values?.documentType),
        documentTypeAlias: normalizeText(values?.documentTypeAlias),
        type_user: activeTypeUser,
      };

      dispatch(showLoader("Loading please wait..."));

      try {
        const response =
          dialogMode === "edit"
            ? await settingsApi.updateDocumentType(getRowId(selectedRow), payload)
            : await settingsApi.createDocumentType(payload);

        if (!isSuccessResponse(response)) {
          throw new Error("Unexpected document type response");
        }

        dispatch(
          showSnackbar({
            message:
              response?.data?.message ||
              (dialogMode === "edit"
                ? "Document type updated successfully"
                : "Document type created successfully"),
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        closeFormDialog();
        await refreshDocumentTypes(activeTypeUser);
      } catch (error) {
        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Error while saving document type",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      } finally {
        dispatch(hideLoader());
      }
    },
    [activeTypeUser, closeFormDialog, dialogMode, dispatch, refreshDocumentTypes, selectedRow]
  );

  const handleDeleteDocumentType = useCallback(async () => {
    if (!selectedRow) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));

    try {
      const response = await settingsApi.deleteDocumentType(getRowId(selectedRow));

      if (!isSuccessResponse(response)) {
        throw new Error("Unexpected delete response");
      }

      dispatch(
        showSnackbar({
          message: response?.data?.message || "Document type deleted successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );

      closeDeleteDialog();
      await refreshDocumentTypes(activeTypeUser);
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Error while deleting document type",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  }, [activeTypeUser, closeDeleteDialog, dispatch, refreshDocumentTypes, selectedRow]);

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      documentType: yup.string().trim().required("Document type is required"),
      documentTypeAlias: yup.string().trim().required("Document type alias is required"),
    }),
    onSubmit: handleSaveDocumentType,
  });

  const normalizedSearch = normalizeKey(searchTerm);

  const rows = useMemo(
    () =>
      (Array.isArray(documentTypes) ? documentTypes : []).map((item, index) => {
        const inferredTypeUser =
          normalizeTypeUser(item?.type_user || item?.typeUser || item?.userType) ||
          activeTypeUser;

        return {
          id: getRowId(item) || index + 1,
          sno: index + 1,
          documentType: normalizeText(item?.documentType),
          documentTypeAlias: normalizeText(item?.documentTypeAlias),
          type_user: inferredTypeUser,
          status: normalizeText(item?.status),
          raw: item,
        };
      }),
    [activeTypeUser, documentTypes]
  );

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) =>
      [row.documentType, row.documentTypeAlias, row.type_user, row.status].some((value) =>
        normalizeKey(value).includes(normalizedSearch)
      )
    );
  }, [normalizedSearch, rows]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredRows.length / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, page, pageSize]);

  const paginatedRows = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, page, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const fromRow = filteredRows.length === 0 ? 0 : page * pageSize + 1;
  const toRow =
    filteredRows.length === 0 ? 0 : Math.min(filteredRows.length, page * pageSize + paginatedRows.length);

  const documentStats = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          const typeUser = normalizeTypeUser(row?.type_user);
          if (typeUser === "DRIVER") {
            acc.driver += 1;
          } else if (typeUser === "USER") {
            acc.user += 1;
          } else if (typeUser === "CORPORATE") {
            acc.corporate += 1;
          } else {
            acc.unknown += 1;
          }

          return acc;
        },
        {
          total: rows.length,
          driver: 0,
          user: 0,
          corporate: 0,
          unknown: 0,
        }
      ),
    [rows]
  );

  const documentColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.NO",
        flex: 0.4,
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant="body2" className={classes.snoCell}>
            {params?.row?.sno}
          </Typography>
        ),
      },
      {
        field: "documentType",
        headerName: "DOCUMENT TYPE",
        flex: 1.5,
        minWidth: 260,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Box className={classes.documentCell}>
              <Box className={classes.documentIcon}>
                <DescriptionOutlinedIcon fontSize="small" />
              </Box>
              <Typography variant="body2" className={classes.documentName} noWrap>
                {row?.documentType || "-"}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "documentTypeAlias",
        headerName: "ALIAS",
        flex: 0.9,
        minWidth: 160,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant="body2" className={classes.aliasCell} noWrap>
            {params?.row?.documentTypeAlias || "-"}
          </Typography>
        ),
      },
      {
        field: "type_user",
        headerName: "TYPE USER",
        flex: 0.8,
        minWidth: 150,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (params) => {
          const meta = getTypeUserMeta(params?.row?.type_user);

          return (
            <Chip
             
              label={meta?.label}
              size="small"
              className={classes.typeChip}
              sx={getTypeUserChipStyles(meta?.tone)}
            />
          );
        },
      },

      {
        field: "actions",
        headerName: "ACTIONS",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Button
              size="small"
              variant="outlined"
              className={classes.rowActionButton}
              endIcon={<KeyboardArrowDownRoundedIcon />}
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
                setMenuRow(row);
              }}
            >
              Action
            </Button>
          );
        },
      },
    ],
    [classes]
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.pageHeader}>
        <Box className={classes.pageHeaderIcon}>
          <DescriptionOutlinedIcon fontSize="small" />
        </Box>
        <Box className={classes.pageHeaderText}>
          <Typography variant="h4" className={classes.pageTitle}>
            Document Types
          </Typography>
          <Typography variant="body2" className={classes.pageSubtitle}>
            Manage document types for DRIVER, USER and CORPORATE.
          </Typography>
        </Box>
      </Box>

      <Box className={classes.typeTabsGrid}>
        {TAB_OPTIONS.map((option) => {
          const isActive = activeTypeUser === option.value;
          const Icon = option.icon;

          return (
            <Paper
              key={option.value}
              variant="outlined"
              role="button"
              tabIndex={0}
              className={`${classes.typeTabCard} ${
                isActive ? classes.typeTabCardActive : ""
              }`}
              onClick={() => handleTabChange(option.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleTabChange(option.value);
                }
              }}
            >
              <Box
                className={classes.typeTabIcon}
                sx={{
                  backgroundColor: isActive ? option.accentSoft : "#eef2ff",
                  color: isActive ? option.accent : "#2563eb",
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Box className={classes.typeTabText}>
                <Typography className={classes.typeTabTitle}>{option.label}</Typography>
                <Typography className={classes.typeTabSubtitle}>{option.subtitle}</Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      <Box className={classes.metricsGrid}>
        <Paper variant="outlined" className={classes.metricCard}>
          <Box className={classes.metricIcon} sx={{ backgroundColor: "#eef4ff", color: "#2563eb" }}>
            <DescriptionOutlinedIcon fontSize="small" />
          </Box>
          <Box className={classes.metricContent}>
            <Typography className={classes.metricLabel}>Total Types</Typography>
            <Typography className={classes.metricValue}>{documentStats.total}</Typography>
            <Typography className={classes.metricHelper}>All document type records</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" className={classes.metricCard}>
          <Box className={classes.metricIcon} sx={{ backgroundColor: "#edfdf3", color: "#15803d" }}>
            <DirectionsCarOutlinedIcon fontSize="small" />
          </Box>
          <Box className={classes.metricContent}>
            <Typography className={classes.metricLabel}>Driver Types</Typography>
            <Typography className={classes.metricValue}>{documentStats.driver}</Typography>
            <Typography className={classes.metricHelper}>type_user = DRIVER</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" className={classes.metricCard}>
          <Box className={classes.metricIcon} sx={{ backgroundColor: "#eef4ff", color: "#2563eb" }}>
            <GroupOutlinedIcon fontSize="small" />
          </Box>
          <Box className={classes.metricContent}>
            <Typography className={classes.metricLabel}>User Types</Typography>
            <Typography className={classes.metricValue}>{documentStats.user}</Typography>
            <Typography className={classes.metricHelper}>type_user = USER</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" className={classes.metricCard}>
          <Box className={classes.metricIcon} sx={{ backgroundColor: "#f5f3ff", color: "#9333ea" }}>
            <CorporateFareOutlinedIcon fontSize="small" />
          </Box>
          <Box className={classes.metricContent}>
            <Typography className={classes.metricLabel}>Corporate Types</Typography>
            <Typography className={classes.metricValue}>{documentStats.corporate}</Typography>
            <Typography className={classes.metricHelper}>type_user = CORPORATE</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" className={classes.metricCard}>
          <Box className={classes.metricIcon} sx={{ backgroundColor: "#f3f4f6", color: "#64748b" }}>
            <HelpOutlineOutlinedIcon fontSize="small" />
          </Box>
          <Box className={classes.metricContent}>
            <Typography className={classes.metricLabel}>Unspecified</Typography>
            <Typography className={classes.metricValue}>{documentStats.unknown}</Typography>
            <Typography className={classes.metricHelper}>Missing type_user</Typography>
          </Box>
        </Paper>
      </Box>

      <Paper variant="outlined" className={classes.toolbarCard}>
        <Box className={classes.toolbarRow}>
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search document type, alias or status..."
            fullWidth
            size="small"
            className={classes.searchField}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" className={classes.searchIcon} />,
            }}
          />

          <Button
            variant="contained"
            onClick={openCreateDialog}
            startIcon={<AddOutlinedIcon />}
            className={classes.addButton}
          >
            Add Document Type
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" className={classes.tableCard}>
        <Box className={classes.tableWrapper}>
          <DataGrid
            rows={paginatedRows}
            columns={documentColumns}
            autoHeight
            disableSelectionOnClick
            getRowId={(row) => row?.id}
            hideFooter
            headerHeight={54}
            rowHeight={58}
            className={classes.dataGridTable}
            loading={loading}
            localeText={{
              noRowsLabel: "No document types found",
            }}
          />
        </Box>
        <Box className={classes.gridFooter}>
          <Typography variant="body2" className={classes.gridFooterLeft}>
            {`Showing ${fromRow} to ${toRow} of ${filteredRows.length} results`}
          </Typography>

          <Box className={classes.gridFooterRight}>
            <Box className={classes.rowsPerPageWrap}>
              <Typography variant="body2" className={classes.rowsPerPageLabel}>
                Rows per page:
              </Typography>
              <TextField
                select
                size="small"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
                className={classes.rowsPerPageSelect}
                SelectProps={{
                  IconComponent: KeyboardArrowDownRoundedIcon,
                }}
              >
                {[10, 25, 50].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Pagination
              count={pageCount}
              page={Math.min(page + 1, pageCount)}
              onChange={(_, value) => setPage(value - 1)}
              className={classes.paginationWrap}
              disabled={filteredRows.length === 0}
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={classes.paginationItem}
                />
              )}
            />
          </Box>
        </Box>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        className={classes.actionMenu}
      >
        <MenuItem onClick={() => openEditDialog(menuRow)}>
          <EditOutlinedIcon fontSize="small" />
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={() => openDeleteDialog(menuRow)}>
          <DeleteOutlineIcon fontSize="small" />
          <Typography variant="body2">Delete</Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={closeFormDialog}
        maxWidth="sm"
        fullWidth
        key={`${dialogMode}-${getRowId(selectedRow) || "new"}-${dialogOpen ? "open" : "closed"}`}
        className={classes.dialogRoot}
      >
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <Typography variant="h6" className={classes.dialogTitle}>
              {dialogMode === "edit"
                ? `Update ${activeTabConfig.label.replace(" Types", "")} Document Type`
                : `Add ${activeTabConfig.label.replace(" Types", "")} Document Type`}
            </Typography>
            <IconButton aria-label="close" onClick={closeFormDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12}>
              <TextField
                name="documentType"
                label="Document Type"
                value={formik.values.documentType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.documentType && formik.errors.documentType)}
                helperText={formik.touched.documentType && formik.errors.documentType}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="documentTypeAlias"
                label="Document Type Alias"
                value={formik.values.documentTypeAlias}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(
                  formik.touched.documentTypeAlias && formik.errors.documentTypeAlias
                )}
                helperText={
                  formik.touched.documentTypeAlias && formik.errors.documentTypeAlias
                }
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeFormDialog} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button variant="contained" onClick={formik.handleSubmit} className={classes.saveButton}>
            {dialogMode === "edit" ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        className={classes.dialogRoot}
      >
        <DialogTitle className={classes.dialogHeader}>
          <Typography variant="h6" className={classes.dialogTitle}>
            Delete Document Type
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.deleteDialogContent}>
          <Typography variant="body2" className={classes.deleteDialogText}>
            Are you sure you want to delete this document type?
          </Typography>
          <Paper variant="outlined" className={classes.deleteSummaryCard}>
            <Typography variant="body2" className={classes.deleteSummaryTitle}>
              {selectedRow?.documentType || "-"}
            </Typography>
            <Typography variant="caption" className={classes.deleteSummarySubtitle}>
              {selectedRow?.documentTypeAlias || "-"}
            </Typography>
          </Paper>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeDeleteDialog} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDocumentType}
            className={classes.dangerButton}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTypesSettings;
